import { gql, useApolloClient, useMutation } from '@apollo/client'
import { AUTH_TOKEN, NATIVE_BASE_URL } from '@constants/storages'
import { useSession } from '@hooks/useSession'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { router } from 'expo-router'
import { isWeb } from 'tamagui'
import { useCourseContextsReset } from '@hooks/useCourseContextsReset'
import { authUpgrade, logout, restoreSession } from '@services/api/auth'
import { unregisterPushDevice } from '@services/push/unregister'

export { useGetUser as useUser } from '@hooks/useGetUser'

interface IAuthInput {
  email: string
  organizationId: number
  password: string
  schoolId: string
}

interface ISignInResponse {
  status: 'success' | 'error'
  error: string | null
  token: string | null
  redirectUrl: string | null
}

const SIGN_OUT = gql`
  mutation UserSignOut {
    userSignOut(input: {}) {
      clientMutationId
      status
    }
  }
`

const USER_SIGN_IN = gql`
  mutation UserSignIn($auth: AuthInputObject!) {
    userSignIn(input: { auth: $auth }) {
      status
      error
      token
      redirectUrl
    }
  }
`

export const useSignOut = () => {
  const { removeItem: removeToken } = useAsyncStorage(AUTH_TOKEN)
  const { removeItem: removeSchoolUrl } = useAsyncStorage(NATIVE_BASE_URL)
  const { logoutSession, token } = useSession()
  const client = useApolloClient()
  const { resetAllContexts } = useCourseContextsReset()

  const [signOutMutation, { data, loading, error }] = useMutation(SIGN_OUT, {
    onCompleted() {
      removeToken().finally(async () => {
        await removeSchoolUrl()
        resetAllContexts()
        await client.clearStore()
        router.setParams({})
        logoutSession()
        router.push('/users/sign_in')
      })
    },
  })

  const signOutMutate = async () => {
    if (!isWeb) {
      await unregisterPushDevice(token)
      return signOutMutation()
    }

    // O Core exige o token CSRF da mesma sessão de cookie para encerrar a sessão.
    // Sem ele, devolve um redirect que o Axios trata como sucesso sem sair.
    let csrfToken: string
    try {
      const { data } = await authUpgrade()
      csrfToken = data.csrf_token
    } catch (error) {
      // O JWT pode continuar válido depois que o cookie de sessão expira.
      // Recria o cookie e busca um CSRF token da nova sessão.
      if (!token) throw error
      await restoreSession(token)
      const { data } = await authUpgrade()
      csrfToken = data.csrf_token
    }
    await logout(csrfToken)
    await removeToken()
    await removeSchoolUrl()
    resetAllContexts()
    // Sem barra final: o pattern do nginx é `^/users/sign_in$`, ancorado.
    window.location.href = '/users/sign_in'
  }

  return { signOutMutate, data, loading, error }
}

export const useSignIn = () => {
  const [signIn, { data, loading, error }] = useMutation<
    { userSignIn: ISignInResponse },
    { auth: IAuthInput }
  >(USER_SIGN_IN)
  return { signIn, data, loading, error }
}
